import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Button,
  Select,
  Tooltip,
  MenuItem,
  Typography,
  FormControl,
} from '@mui/material';
import _ from 'lodash';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import CmLayers from './CmLayers';
import HighlightButton from './HighlightButton';
import PreviewButton, { PreviewButtonRef } from './PreviewButton';

import { RootState, TroubleTicket } from 'types/types';
import { selectDashboardById } from 'features/dashboardsState';
import { fetchCellTroubleTickets } from 'utils/Map/mapService';
import { getFiltersByDashboardId } from 'features/filtersState';
import { setHighlightedCriterias } from 'features/advancedMapState';
import { TroubleTicketsTableRef } from '../TroubleTickets/TroubleTicketsTable';

interface CellDetailsProps {
  cell: any;
  kpiMarkers: any[];
  recenterClickedCellSite: (selectedItem: any) => void;
  siteStreetView: () => void;
  selectedKpiValues: string[];
  detailsCmDump: (any) => void;
  activeElementDetailsLayers: {};
  displayHeightGraph: () => void;
  hideElementDetailLayer: (
    networkElementMarkerInfo: any,
    elementDetailLayerDefinition: any
  ) => void;
  handleCloseCellDetails: () => void;
  handleElementDetailsLayerClicked: (
    networkElementMarkerInfo: any,
    elementDetailLayerDefinition: any
  ) => void;
  detailsPerformanceDetails: (any) => void;
  previewButtonRef: React.RefObject<PreviewButtonRef>;
  troubleTicketsTableRef: React.RefObject<TroubleTicketsTableRef>;
  kqiFiltersSelected: React.MutableRefObject<{ id: string; name: string }[]>;
}

const fmKpiIds = [
  'FM_ALARMS_FM_TOTAL_ALARMS',
  'FM_ALARMS_FM_TOTAL_ALARMS_ACTIVE',
  'FM_ALARMS_FM_TOTAL_ALARMS_CLEARED',
  'FM_ALARMS_FM_TOTAL_ALARMS_CRITICAL',
  'FM_ALARMS_FM_TOTAL_ALARMS_MAJOR',
  'FM_ALARMS_FM_TOTAL_ALARMS_MINOR',
  'FM_ALARMS_FM_TOTAL_ALARMS_WARNING',
];

const dashboardId = 'ni-ran-advanced-map-dashboard';

const troubleTickets = 'trouble tickets';

const CellDetails: React.FC<CellDetailsProps> = ({
  cell,
  kpiMarkers,
  recenterClickedCellSite,
  detailsCmDump,
  siteStreetView,
  previewButtonRef,
  selectedKpiValues,
  displayHeightGraph,
  kqiFiltersSelected,
  handleCloseCellDetails,
  troubleTicketsTableRef,
  hideElementDetailLayer,
  detailsPerformanceDetails,
  activeElementDetailsLayers,
  handleElementDetailsLayerClicked,
}) => {
  const dispatch = useDispatch();

  const dashboard = useSelector((state: RootState) =>
    selectDashboardById(state, 'ni-ran-advanced-map-dashboard')
  );

  const highlightedCriterias = useSelector(
    (state: RootState) => state.advancedMap.highlightedCriterias
  );

  const { granularityDates } = useSelector(
    (state: RootState) => state.advancedMap
  );

  const filtersState = useSelector((state: any) =>
    getFiltersByDashboardId(state, dashboardId)
  );

  const troubleTicketsRef = useRef<TroubleTicket[]>([]);
  const troubleTicketsDataLoadedRef = useRef<boolean>(false);

  const [rowLimit, setRowLimit] = useState<number>(5); // Limit the number of rows displayed
  const [itemEntity, setItemEntity] = useState({}); // State for item entity
  const [selectedSite, setSelectedSite] = useState(''); // State for selected site
  const [excludedKeys, setExcludedKeys] = useState<string[]>([
    ...selectedKpiValues,
    ...selectedKpiValues.map((kpi) => 'SITE_KPI_' + kpi),
    ...kqiFiltersSelected.current.map((kqi) => kqi.id),
    ...kqiFiltersSelected.current.map((kqi) => 'SITE_KQI_' + kqi.id),
    'SITE_NATURAL_KEY',
    'type',
    'CELL ID',
    'id',
    'CELL_BAND_NAME',
    'location',
    'THRESHOLD_COLOR',
    'THRESHOLD_VALUES',
    'SITE_KPI_THRESHOLD_VALUES',
    'SITE_KPI_THRESHOLD_COLOR',
  ]); // State for excluded keys

  const showMore = () => {
    setRowLimit(
      rowLimit < Object.entries(itemEntity).length
        ? Object.entries(itemEntity).length
        : 5
    );
  };

  const handleChange = (event) => {
    const selectedKey = event.target.value;
    setSelectedSite(selectedKey);
  };

  const handleHighlightItem = (id: string, value: string) => {
    if (highlightedCriterias) {
      const index = highlightedCriterias.findIndex(
        (item) => item.id === id && item.value === value
      );
      const highlightedCriteriasClone = [...highlightedCriterias];
      if (index !== -1) {
        highlightedCriteriasClone.splice(index, 1);
      } else {
        highlightedCriteriasClone.push({ id, value });
      }
      dispatch(setHighlightedCriterias(highlightedCriteriasClone));
    }
  };

  const handleCheckCriteriaHighlighted = (id: string, value: string) => {
    const index = highlightedCriterias.findIndex(
      (item) => item.id === id && item.value === value
    );
    if (index !== -1) {
      return false;
    }
    return true;
  };

  const fetchTroubleTicketData = async () => {
    let locationFilter = filtersState.find(
      (filter) => filter.category === 'regionName'
    );
    const region = locationFilter?.selectedValues?.[0].id;

    const response = await fetchCellTroubleTickets(
      cell.id ?? '',
      cell.type === 'site'
        ? cell['sites'][selectedSite].SITE_NATURAL_KEY ?? ''
        : '',
      region ?? '',
      cell.type === 'site'
        ? cell['sites'][selectedSite].TECHNOLOGY ?? ''
        : cell['TECHNOLOGY'] ?? '',
      granularityDates
    );

    return response.data;
  };

  const handlePreviewTroubleTicket = () => async () => {
    try {
      let troubleTickets = troubleTicketsRef.current;

      if (!troubleTicketsDataLoadedRef.current) {
        previewButtonRef.current?.handleLoader(true);
        troubleTickets = await fetchTroubleTicketData();
        troubleTicketsDataLoadedRef.current = true;
        troubleTicketsRef.current = troubleTickets;
      }

      troubleTicketsTableRef.current?.mapPopupRef.current?.handleOpenModal(
        true
      );

      setTimeout(() => {
        troubleTicketsTableRef.current?.handleTroubleTicketsData(
          troubleTickets
        );
      }, 200);
    } catch (error) {
      console.error('Something went wrong ', error);
    } finally {
      previewButtonRef.current?.handleLoader(false);
    }
  };

  const handleClosePreview = () =>
    troubleTicketsTableRef.current?.mapPopupRef.current?.handleOpenModal(false);

  const getCellPropertyKey = (key: string) => {
    if (key.toLowerCase().includes(troubleTickets) && cell.type === 'site') {
      return key.replace('SITE', '').trim();
    } else if (key.toLowerCase().includes(troubleTickets)) {
      return key.replace('CELL', '').trim();
    }
    return key;
  };

  const troubleTicketsPropertyBasedOnTypeCondition = (key: string) => {
    return (
      (cell.type == 'site' &&
        !key.toLowerCase().includes('cell trouble tickets count')) ||
      (cell.type == null &&
        !key.toLowerCase().includes('site trouble tickets count'))
    );
  };

  const handleResetPreviewTroubleTickets = () => {
    handleClosePreview();
    troubleTicketsRef.current = [];
    troubleTicketsDataLoadedRef.current = false;
    previewButtonRef.current?.handlePreviewSelected(false);
  };

  useEffect(() => {
    if (cell.type === 'site') {
      setExcludedKeys((prevExcludedKeys) => [
        ...prevExcludedKeys,
        'CELL ANTENNA HEIGHT',
        'CELL BAND',
        'CELL BAND Name',
        'CELL CATEGORY',
        'CELL CLASS',
        'DEPLOYMENT STATUS',
        'CELL ID',
        'CELL Name',
        'CELL_BAND_NAME',
        'ECI/NCI',
        'MECHANICAL TILT',
        'E-UTRAN CELL IDENTIFIER',
        'ROOT SEQUENCE INDEX',
        'SWVERSION',
        'PHYSICAL CELL ID',
        'TRACKING AREA CODE',
        'NR CELL IDENTIFIER',
        'LOGICAL ROOTSEQUENCE INDEX',
        'NR PHYSICAL CELL ID',
        'NR TRACKING AREA CODE',
        'ARFCNDL',
        'ARFCNUL',
        'SSB DURATION',
        'SSB FREQUENCY',
        'SSB OFFSET',
        'SSB PERIODICITY',
        'UL FREQUENCY',
        'DL FREQUENCY',
      ]);
      const keys = Object.keys(cell['sites']); // Get the keys of the object
      const selectedKey = keys[cell.siteIndex];
      setSelectedSite(selectedKey);
    } else {
      setExcludedKeys([
        ...selectedKpiValues,
        ...selectedKpiValues.map((kpi) => 'SITE_KPI_' + kpi),
        ...kqiFiltersSelected.current.map((kqi) => kqi.id),
        ...kqiFiltersSelected.current.map((kqi) => 'SITE_KQI_' + kqi.id),
        'SITE_NATURAL_KEY',
        'type',
        'CELL ID',
        'id',
        'CELL_BAND_NAME',
        'location',
        'THRESHOLD_COLOR',
        'THRESHOLD_VALUES',
        'SITE_KPI_THRESHOLD_VALUES',
        'SITE_KPI_THRESHOLD_COLOR',
      ]);
      setItemEntity({ ...cell.contextInfo, id: cell.id });
    }
    handleResetPreviewTroubleTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell]);

  useEffect(() => {
    if (cell.type === 'site' && selectedSite) {
      const siteKPIMarker = kpiMarkers.find(
        (el) =>
          el['SITE NATURAL KEY'] ==
          cell['sites'][selectedSite]['SITE_NATURAL_KEY']
      );

      let siteTroubleTickets = {};

      if (siteKPIMarker && siteKPIMarker['SITE TROUBLE TICKETS COUNT']) {
        siteTroubleTickets = {
          'SITE TROUBLE TICKETS COUNT':
            siteKPIMarker['SITE TROUBLE TICKETS COUNT'] ?? '',
        };
      }

      setItemEntity({
        ...cell['sites'][selectedSite],
        ...siteTroubleTickets,
        type: cell.type,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  return (
    <Box component={Paper}>
      <>
        <Box className="cell-details-top-section">
          <Box className="map-popup-header">
            <Close onClick={handleCloseCellDetails} className="close-markers" />
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {cell.type === 'site' ? 'Site' : 'Cell'} Details
              {cell.type === 'site' ? '' : `: ID ${cell.id}`}
            </Typography>
          </Box>

          {cell.type === 'site' && (
            <Box className="cell-details-entry">
              <FormControl fullWidth>
                <Select
                  value={selectedSite}
                  onChange={handleChange}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Select Site' }}
                >
                  {Object.keys(cell['sites']).map((key, index) => (
                    <MenuItem key={key} value={key}>
                      <Box>
                        <Typography variant="body2">
                          {cell['sites'][key]['SITE NAME']} ({cell['sites'][key]['TECHNOLOGY']})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box className="cell-details-list">
            {Object.entries(itemEntity)
              .filter(
                ([key]) =>
                  !excludedKeys.includes(key) &&
                  !key.startsWith('FM_ALARMS_') &&
                  !key.startsWith('SITE_FM_ALARMS_') &&
                  troubleTicketsPropertyBasedOnTypeCondition(key)
              )
              .slice(0, rowLimit)
              .map(([key, value], index) => (
                <>
                  <Box className="cell-details-entry" key={index}>
                    <Grid
                      container
                      columnSpacing={2}
                      sx={{ height: '30px' }}
                      alignItems={'center'}
                    >
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          {getCellPropertyKey(key)}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {(value as string).length >= 17 ? (
                          <Tooltip
                            title={value as string}
                            arrow
                            placement="top"
                          >
                            <Typography variant="body2" noWrap>
                              {value as string}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" noWrap>
                            {value as string}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={1}>
                        {(
                          dashboard?.descriptor.settings
                            .cellsForHighlight as string[]
                        ).includes(key) && (
                          <HighlightButton
                            id={key}
                            value={value as string}
                            handleHighlightItem={handleHighlightItem}
                            highlighted={handleCheckCriteriaHighlighted(
                              key,
                              value as string
                            )}
                          />
                        )}
                        {key.toLowerCase().includes(troubleTickets) && (
                          <PreviewButton
                            ref={previewButtonRef}
                            handleClosePreview={handleClosePreview}
                            preview={troubleTicketsRef.current.length > 0}
                            handlePreviewItem={handlePreviewTroubleTicket()}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ))}
            <Grid container columnSpacing={1}>
              <Grid item xs={12}>
                {selectedKpiValues.map((kpi) => {
                  let siteKpiPrefix = cell.type === 'site' ? 'SITE_KPI_' : '';
                  let kpiNameCopy = siteKpiPrefix + kpi;
                  if (
                    itemEntity[kpiNameCopy] &&
                    itemEntity[kpiNameCopy] !== ''
                  ) {
                    return (
                      <Box className="ea-layers" key={kpiNameCopy}>
                        <Grid container columnSpacing={0}>
                          <Grid item xs={12} className="ea-layer-entry">
                            <Box className="cell-details-entry">
                              <Typography variant="body2">
                                {kpiNameCopy.replace('SITE_KPI_', '')}:{' '}
                                {itemEntity[kpiNameCopy]}
                              </Typography>
                              {itemEntity[siteKpiPrefix + 'THRESHOLD_VALUES'] &&
                                itemEntity[
                                  siteKpiPrefix + 'THRESHOLD_VALUES'
                                ].map((item) => {
                                  return (
                                    <Box
                                      key={item.name}
                                      className="legend-entry"
                                    >
                                      <Box
                                        className="square"
                                        sx={{
                                          backgroundColor: item.color
                                            ? item.color
                                            : '#fff',
                                        }}
                                      ></Box>
                                      <Box>
                                        {' '}
                                        <strong>{item.name}: </strong>{' '}
                                        {item.value}
                                      </Box>
                                    </Box>
                                  );
                                })}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }
                })}
                {kqiFiltersSelected.current.map((kqi) => {
                  let siteKqiPrefix = cell.type === 'site' ? 'SITE_KQI_' : '';
                  let kqiNameCopy = siteKqiPrefix + kqi.id;
                  if (
                    itemEntity[kqiNameCopy] &&
                    itemEntity[kqiNameCopy] !== ''
                  ) {
                    return (
                      <Box className="ea-layers" key={kqiNameCopy}>
                        <Grid container columnSpacing={0}>
                          <Grid item xs={12} className="ea-layer-entry">
                            <Box className="cell-details-entry">
                              <Typography variant="body2">
                                {kqiNameCopy.replace('SITE_KQI_', '')}:{' '}
                                {itemEntity[kqiNameCopy]}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }
                })}
                {fmKpiIds.map((fmKpiId) => {
                  // The entris in the Object will be like:
                  //  FM_ALARMS_<FM alarm Id>  Or SITE_FM_ALARMS_<FM alarm Id>
                  let siteKpiPrefix = cell.type === 'site' ? 'SITE_' : '';
                  let fmKpiIdCopy = siteKpiPrefix + fmKpiId;
                  if (
                    itemEntity[fmKpiIdCopy] &&
                    itemEntity[fmKpiIdCopy] !== ''
                  ) {
                    return (
                      <Box className="ea-layers" key={fmKpiIdCopy}>
                        <Grid container columnSpacing={0}>
                          <Grid item xs={12} className="ea-layer-entry">
                            <Box className="cell-details-entry">
                              <Typography variant="body2">
                                {fmKpiIdCopy
                                  .replace('SITE_', '')
                                  .replace('FM_ALARMS_', '')}
                                : {itemEntity[fmKpiIdCopy]}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }
                })}
              </Grid>
            </Grid>
            <Grid container columnSpacing={2} className="cell-details-buttons">
              <Grid item xs={6}>
                <Button variant="contained" onClick={() => recenterClickedCellSite(cell)}>
                  Re-Center Cell
                </Button>
              </Grid>
              <Grid item xs={6}>
                {rowLimit < Object.entries(itemEntity).length ? (
                  <Button variant="contained" onClick={showMore}>
                    Show More
                  </Button>
                ) : (
                  <Button variant="contained" onClick={showMore}>
                    Show Less
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box>
          <Grid
            container
            columnSpacing={2}
            className="cell-details-buttons cell-details-top-level-buttons"
          >
            {cell.type !== 'site' && (
              <Grid item xs={4}>
                <Button variant="contained" onClick={displayHeightGraph}>
                  Elevation graph
                </Button>
              </Grid>
            )}
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() => detailsCmDump(itemEntity)}
              >
                CM dump
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() => detailsPerformanceDetails(itemEntity)}
              >
                RAN Dashboard
              </Button>
            </Grid>
            {cell.type === 'site' && (
              <Grid item xs={4}>
                <Button variant="contained" onClick={siteStreetView}>
                  Street view
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
        <CmLayers
          handleElementDetailsLayerClicked={handleElementDetailsLayerClicked}
          networkElementMarkerInfo={itemEntity}
          activeElementDetailsLayers={activeElementDetailsLayers}
          hideElementDetailLayer={hideElementDetailLayer}
        />
      </>
    </Box>
  );
};

export default CellDetails;
